import { useState } from "react"
import { Box, HStack, Text, VStack } from "@chakra-ui/react"
import { FiChevronDown, FiChevronRight } from "react-icons/fi"
import { formatAssetClass } from "@hydrologiq/shared/asset/PaginatedTable"
import { AlertSettingsCard } from "@hydrologiq/shared/components/alerts/AlertSettingsCard"
import { AlignedTitles } from "@hydrologiq/shared/components/TitleWithChips"
import { AlertRuleSummary, OriginTag, SeverityTag } from "@hydrologiq/shared/components/alerts/alertSummary"
import { ScopeChip, ScopeLinkChip } from "@hydrologiq/shared/components/ScopeLinkChip"
import { ManagedByChip } from "@hydrologiq/shared/data-connections/ManagedByChip"
import type { AssetAlertGroup as AssetAlertGroupType } from "../../hooks/useAlertConfiguration"

const MANAGED_ON_ASSET =
  "This alert belongs to the asset. Switch it on or off and change it on the asset's Alert Configuration tab."

interface AssetAlertGroupProps {
  group: AssetAlertGroupType
}

/**
 * One Asset of the System and every alert it and its Modules have, read-only: alerts are
 * managed on the Asset.
 */
export function AssetAlertGroup({ group }: AssetAlertGroupProps) {
  const [expanded, setExpanded] = useState(false)

  if (group.totalCount === 0) return null

  const moduleNames = Object.fromEntries(group.modules.map((m) => [m.id, m.name]))

  return (
    <Box borderWidth="1px" borderRadius="md" overflow="hidden">
      <HStack
        px={4}
        py={3}
        cursor="pointer"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        _hover={{ bg: "bg.subtle" }}
        justify="space-between"
      >
        <HStack gap={2} align="center">
          {expanded ? <FiChevronDown /> : <FiChevronRight />}
          <Text fontSize="md" fontWeight="semibold">
            {group.asset.name}
          </Text>
          <ScopeLinkChip
            label="Asset"
            href={`/assets/${group.asset.id}/settings?tab=alert-configuration`}
            entityName={`${group.asset.name} alert configuration`}
          />
          {group.asset.class && <ScopeChip>{formatAssetClass(group.asset.class)}</ScopeChip>}
        </HStack>
        <ScopeChip>
          {group.activeCount} active, {group.inactiveCount} inactive
        </ScopeChip>
      </HStack>

      {expanded && (
        <AlignedTitles>
        <VStack gap={2} align="stretch" p={2}>
          {group.rules.map((rule) => {
            const moduleName = moduleNames[rule.targetAssetId ?? ""]
            const custom = rule.template.origin?.toUpperCase() === "CUSTOM"
            return (
              <AlertSettingsCard
                key={`${rule.template.id}:${rule.targetAssetId}`}
                label={rule.template.name}
                title={rule.template.name}
                isEnabled={rule.isEnabled}
                chips={
                  <>
                    <OriginTag custom={custom} />
                    <SeverityTag severity={rule.template.severity} />
                    {moduleName && (
                      <>
                        <ScopeLinkChip
                          label="Module"
                          href={`/assets/${rule.targetAssetId}/settings?tab=alert-configuration`}
                          entityName={moduleName}
                        />
                        <ScopeChip>{moduleName}</ScopeChip>
                      </>
                    )}
                    <ManagedByChip scope="asset" tooltip={MANAGED_ON_ASSET} />
                  </>
                }
                summary={<AlertRuleSummary rule={rule} assetNames={moduleNames} />}
              />
            )
          })}
        </VStack>
        </AlignedTitles>
      )}
    </Box>
  )
}

export default AssetAlertGroup
